import React, { useState, useEffect } from 'react';
import { Trophy, RefreshCw, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, X, Play, Settings, Clock, Shield, ChevronDown, ChevronRight, Crown, Users, RotateCcw } from 'lucide-react';
import { BracketGenerationService, type SeedingMethod } from '../services/BracketGenerationService';
import { MatchProgressionService, type MatchWithDetails, type BracketConfig } from '../services/MatchProgressionService';

interface AdminBracketManagerProps {
  tournamentId: string;
  onClose?: () => void;
}

interface ConfirmModal {
  title: string;
  message: string;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: () => void;
}

function getRoundLabel(round: number, totalRounds: number): string {
  const remaining = totalRounds - round + 1;
  if (totalRounds === 0) return `Round ${round}`;
  if (remaining === 1) return 'Final';
  if (remaining === 2) return 'Semifinals';
  if (remaining === 3) return 'Quarterfinals';
  return `Round ${round}`;
}

function formatDeadline(deadline: string | null): { label: string; urgent: boolean } {
  if (!deadline) return { label: 'No deadline', urgent: false };
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return { label: 'Expired', urgent: true };
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours < 1) return { label: `${mins}m remaining`, urgent: true };
  if (hours < 6) return { label: `${hours}h ${mins}m remaining`, urgent: true };
  if (hours < 24) return { label: `${hours}h remaining`, urgent: false };
  const days = Math.floor(hours / 24);
  return { label: `${days}d ${hours % 24}h remaining`, urgent: false };
}

function getForfeitLabel(match: MatchWithDetails): string {
  if (match.forfeit_reason === 'timeout_one_submission') return 'Timeout (one submitted)';
  if (match.forfeit_reason === 'timeout_both_no_submission') return 'Timeout (neither submitted)';
  if (match.forfeit_reason === 'manual') return 'Manual forfeit';
  return 'Forfeited';
}

interface RoundSectionProps {
  roundNumber: number;
  totalRounds: number;
  matches: MatchWithDetails[];
  onForfeit: (matchId: string, participantId: string, participantName: string) => void;
  onReset: (matchId: string, matchNumber: number) => void;
  loading: boolean;
}

const RoundSection: React.FC<RoundSectionProps> = ({
  roundNumber,
  totalRounds,
  matches,
  onForfeit,
  onReset,
  loading,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const completedCount = matches.filter(m => m.match_status === 'completed' || m.match_status === 'forfeited').length;
  const pendingCount = matches.length - completedCount;
  const roundLabel = getRoundLabel(roundNumber, totalRounds);
  const isAllDone = pendingCount === 0;

  return (
    <div className="border border-slate-600 rounded-lg overflow-hidden">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between p-4 bg-slate-700 hover:bg-slate-650 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {collapsed ? <ChevronRight size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          <div>
            <span className="font-semibold text-white">{roundLabel}</span>
            <span className="text-gray-400 ml-2 text-sm">Round {roundNumber}</span>
          </div>
          {isAllDone && (
            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full border border-green-500/30 flex items-center gap-1">
              <CheckCircle size={11} />
              Complete
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm">
          {pendingCount > 0 && (
            <span className="text-yellow-400 flex items-center gap-1">
              <Clock size={14} />
              {pendingCount} pending
            </span>
          )}
          <span className="text-gray-400">
            {completedCount}/{matches.length} done
          </span>
        </div>
      </button>

      {!collapsed && (
        <div className="divide-y divide-slate-700">
          {matches.map(match => (
            <MatchRow
              key={match.id}
              match={match}
              onForfeit={onForfeit}
              onReset={onReset}
              loading={loading}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface MatchRowProps {
  match: MatchWithDetails;
  onForfeit: (matchId: string, participantId: string, participantName: string) => void;
  onReset: (matchId: string, matchNumber: number) => void;
  loading: boolean;
}

const MatchRow: React.FC<MatchRowProps> = ({ match, onForfeit, onReset, loading }) => {
  const isCompleted = match.match_status === 'completed';
  const isForfeited = match.match_status === 'forfeited';
  const isDone = isCompleted || isForfeited;

  const p1Name = match.participant1?.username ?? 'TBD';
  const p2Name = match.participant2?.username ?? 'TBD';
  const p1IsWinner = isDone && match.winner_id === match.participant1_id;
  const p2IsWinner = isDone && match.winner_id === match.participant2_id;

  const deadline = formatDeadline(match.submission_deadline);

  const statusColors: Record<string, string> = {
    pending: 'bg-slate-600 text-gray-300',
    one_submitted: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    both_submitted: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
    scoring: 'bg-orange-500/20 text-orange-300 border border-orange-500/30',
    completed: 'bg-green-500/20 text-green-300 border border-green-500/30',
    forfeited: 'bg-red-500/20 text-red-300 border border-red-500/30',
  };

  const statusLabel: Record<string, string> = {
    pending: 'Pending',
    one_submitted: 'One Submitted',
    both_submitted: 'Both Submitted',
    scoring: 'Scoring',
    completed: 'Completed',
    forfeited: 'Forfeited',
  };

  return (
    <div className="p-4 bg-slate-800">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-500 font-mono">Match {match.match_number}</span>
        <div className="flex items-center gap-2">
          {isForfeited && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Shield size={11} />
              {getForfeitLabel(match)}
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[match.match_status] ?? statusColors.pending}`}>
            {statusLabel[match.match_status] ?? match.match_status}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <ParticipantRow
          name={p1Name}
          score={isDone ? match.participant1_entry?.score ?? null : null}
          isWinner={p1IsWinner}
          isLoser={isDone && !p1IsWinner && !!match.participant1_id}
          submitted={!!match.participant1_recording_id}
          matchDone={isDone}
        />
        <div className="text-center text-xs text-gray-600 font-medium">vs</div>
        <ParticipantRow
          name={p2Name}
          score={isDone ? match.participant2_entry?.score ?? null : null}
          isWinner={p2IsWinner}
          isLoser={isDone && !p2IsWinner && !!match.participant2_id}
          submitted={!!match.participant2_recording_id}
          matchDone={isDone}
        />
      </div>

      {!isDone && match.submission_deadline && (
        <div className={`mt-3 flex items-center gap-1 text-xs ${deadline.urgent ? 'text-red-400' : 'text-gray-400'}`}>
          <Clock size={12} />
          {deadline.label}
        </div>
      )}

      {(match.participant1_id || match.participant2_id) && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {!isDone && match.participant1_id && (
            <button
              onClick={() => onForfeit(match.id, match.participant1_id!, p1Name)}
              disabled={loading}
              className="flex-1 px-2 py-1.5 text-xs bg-red-900/40 hover:bg-red-800/60 border border-red-700/40 text-red-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Forfeit {p1Name}
            </button>
          )}
          {!isDone && match.participant2_id && (
            <button
              onClick={() => onForfeit(match.id, match.participant2_id!, p2Name)}
              disabled={loading}
              className="flex-1 px-2 py-1.5 text-xs bg-red-900/40 hover:bg-red-800/60 border border-red-700/40 text-red-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Forfeit {p2Name}
            </button>
          )}
          <button
            onClick={() => onReset(match.id, match.match_number)}
            disabled={loading}
            className="px-3 py-1.5 text-xs bg-amber-900/40 hover:bg-amber-800/60 border border-amber-700/40 text-amber-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            title="Reset match — clears submissions, restores both participants, resets the timer"
          >
            <RotateCcw size={11} />
            Reset Match
          </button>
        </div>
      )}
    </div>
  );
};

interface ParticipantRowProps {
  name: string;
  score: number | null;
  isWinner: boolean;
  isLoser: boolean;
  submitted: boolean;
  matchDone: boolean;
}

const ParticipantRow: React.FC<ParticipantRowProps> = ({
  name,
  score,
  isWinner,
  isLoser,
  submitted,
  matchDone,
}) => {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
        isWinner
          ? 'bg-green-500/15 border border-green-500/30'
          : isLoser
          ? 'bg-red-500/10 border border-red-500/20 opacity-70'
          : 'bg-slate-700 border border-slate-600'
      }`}
    >
      <div className="flex items-center gap-2">
        {isWinner && <Crown size={14} className="text-yellow-400 shrink-0" />}
        <span className={`font-medium ${isWinner ? 'text-white' : isLoser ? 'text-gray-400' : 'text-gray-200'}`}>
          {name}
        </span>
        {!matchDone && submitted && (
          <span className="text-xs text-green-400 flex items-center gap-0.5">
            <CheckCircle size={11} />
            Submitted
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {score !== null && (
          <span className={`font-bold tabular-nums ${isWinner ? 'text-green-400' : 'text-gray-400'}`}>
            {score}
          </span>
        )}
        {isWinner && <span className="text-xs text-green-400 font-semibold">WIN</span>}
        {isLoser && <span className="text-xs text-red-400/70">OUT</span>}
      </div>
    </div>
  );
};

const AdminBracketManager: React.FC<AdminBracketManagerProps> = ({ tournamentId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [seedingMethod, setSeedingMethod] = useState<SeedingMethod>('random');
  const [bracketGenerated, setBracketGenerated] = useState(false);
  const [matches, setMatches] = useState<MatchWithDetails[]>([]);
  const [bracketConfig, setBracketConfig] = useState<BracketConfig | null>(null);
  const [tournamentData, setTournamentData] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModal | null>(null);

  useEffect(() => {
    loadAll();
  }, [tournamentId]);

  const loadAll = async () => {
    await Promise.all([loadTournamentData(), checkBracketStatus()]);
  };

  const loadTournamentData = async () => {
    try {
      const data = await tournamentService.getTournamentWithEntries(tournamentId);
      setTournamentData(data);
    } catch {
    }
  };

  const checkBracketStatus = async () => {
    try {
      const [bracketData, config] = await Promise.all([
        MatchProgressionService.getTournamentBracket(tournamentId),
        MatchProgressionService.getBracketConfig(tournamentId),
      ]);
      if (bracketData.matches.length > 0) {
        setBracketGenerated(true);
        setMatches(bracketData.matches);
        setBracketConfig(config);
      }
    } catch {
      setBracketGenerated(false);
    }
  };

  const handleGenerateBracket = async () => {
    try {
      setLoading(true);
      setMessage(null);
      await BracketGenerationService.generateBracket(tournamentId, seedingMethod, true);
      setMessage({ type: 'success', text: 'Bracket generated! Registration locked and tournament activated.' });
      setBracketGenerated(true);
      await loadAll();
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to generate bracket' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBracket = () => {
    setConfirmModal({
      title: 'Regenerate Bracket',
      message: 'This will reset all matches and reassign seeds. All current match progress will be lost. Are you sure?',
      confirmLabel: 'Regenerate',
      confirmClass: 'bg-yellow-600 hover:bg-yellow-700',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setLoading(true);
          setMessage(null);
          await BracketGenerationService.regenerateBracket(tournamentId, seedingMethod, true);
          setMessage({ type: 'success', text: 'Bracket regenerated! Registration locked and tournament activated.' });
          await checkBracketStatus();
        } catch (error) {
          setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to regenerate bracket' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleResetRequest = (matchId: string, matchNumber: number) => {
    setConfirmModal({
      title: 'Reset Match',
      message: `Reset Match ${matchNumber}? This will clear both submissions, restore both participants, and reset the round timer so they can resubmit.`,
      confirmLabel: 'Reset Match',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setLoading(true);
          setMessage(null);
          const result = await MatchProgressionService.resetMatch(matchId);
          if (result.success) {
            setMessage({ type: 'success', text: `Match ${matchNumber} has been reset. Both participants can now resubmit.` });
            await checkBracketStatus();
          } else {
            setMessage({ type: 'error', text: result.message || 'Failed to reset match' });
          }
        } catch (error) {
          setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to reset match' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleForfeitRequest = (matchId: string, participantId: string, participantName: string) => {
    setConfirmModal({
      title: 'Forfeit Match',
      message: `Forfeit ${participantName} from this match? Their opponent will advance automatically.`,
      confirmLabel: 'Forfeit',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setLoading(true);
          setMessage(null);
          const result = await MatchProgressionService.forfeitMatch(matchId, participantId);
          if (result.success) {
            setMessage({ type: 'success', text: `${participantName} has been forfeited.` });
            await checkBracketStatus();
          } else {
            setMessage({ type: 'error', text: result.message || 'Failed to forfeit match' });
          }
        } catch (error) {
          setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to forfeit match' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const registrationLocked = tournamentData?.registration_locked;
  const participantCount = tournamentData?.tournament_entries?.length ?? 0;
  const totalRounds = bracketConfig?.totalRounds ?? 0;

  const roundGroups: Record<number, MatchWithDetails[]> = {};
  matches.forEach(m => {
    if (!roundGroups[m.round_number]) roundGroups[m.round_number] = [];
    roundGroups[m.round_number].push(m);
  });
  const sortedRounds = Object.keys(roundGroups)
    .map(Number)
    .sort((a, b) => a - b);

  const activeRound = sortedRounds.find(r =>
    roundGroups[r].some(m => m.match_status !== 'completed' && m.match_status !== 'forfeited')
  );
  const completedRounds = sortedRounds.filter(r =>
    roundGroups[r].every(m => m.match_status === 'completed' || m.match_status === 'forfeited')
  ).length;

  return (
    <>
      <div className="p-6 bg-slate-800 rounded-lg text-white">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings size={24} />
            Bracket Management
          </h2>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded transition-colors">
              <X size={20} />
            </button>
          )}
        </div>

        {message && (
          <div
            className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-700 border border-green-500 text-white'
                : 'bg-red-700 border border-red-500 text-white'
            }`}
          >
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            <span className="text-sm font-medium">{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-auto p-1 hover:bg-white/20 rounded-full">
              <X size={14} />
            </button>
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-slate-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Users size={18} />
              Tournament Status
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-800 p-3 rounded-lg">
                <div className="text-gray-400 text-xs mb-1">Participants</div>
                <div className="text-xl font-bold">{participantCount}</div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <div className="text-gray-400 text-xs mb-1">Registration</div>
                <div className={`text-sm font-semibold ${registrationLocked ? 'text-red-400' : 'text-green-400'}`}>
                  {registrationLocked ? 'Locked' : 'Open'}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <div className="text-gray-400 text-xs mb-1">Bracket</div>
                <div className={`text-sm font-semibold ${bracketGenerated ? 'text-green-400' : 'text-yellow-400'}`}>
                  {bracketGenerated ? 'Generated' : 'Not Generated'}
                </div>
              </div>
              {bracketGenerated && totalRounds > 0 && (
                <div className="bg-slate-800 p-3 rounded-lg">
                  <div className="text-gray-400 text-xs mb-1">Rounds</div>
                  <div className="text-sm font-semibold">
                    <span className="text-green-400">{completedRounds}</span>
                    <span className="text-gray-500"> / {totalRounds} complete</span>
                  </div>
                </div>
              )}
            </div>

            {bracketGenerated && activeRound !== undefined && (
              <div className="mt-3 px-3 py-2 bg-orange-500/15 border border-orange-500/30 rounded-lg text-sm flex items-center gap-2 text-orange-300">
                <Clock size={14} />
                Active: {getRoundLabel(activeRound, totalRounds)} (Round {activeRound})
                <span className="ml-auto text-xs text-gray-400">
                  {roundGroups[activeRound].filter(m => m.match_status === 'completed' || m.match_status === 'forfeited').length}/
                  {roundGroups[activeRound].length} matches done
                </span>
              </div>
            )}

          </div>

          <div className="bg-slate-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Trophy size={18} />
              Bracket Generation
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Seeding Method</label>
              <select
                value={seedingMethod}
                onChange={e => setSeedingMethod(e.target.value as SeedingMethod)}
                className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white"
                disabled={loading}
              >
                <option value="random">Random Seeding</option>
                <option value="score_based">Score-Based Seeding</option>
                <option value="manual">Manual Seeding</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {seedingMethod === 'random' && 'Participants will be randomly ordered'}
                {seedingMethod === 'score_based' && 'Participants ordered by highest previous score'}
                {seedingMethod === 'manual' && 'Uses manually set seed numbers'}
              </p>
            </div>

            {!bracketGenerated ? (
              <button
                onClick={handleGenerateBracket}
                disabled={loading || participantCount < 2}
                className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                <Play size={18} />
                {loading ? 'Generating...' : 'Generate Bracket'}
              </button>
            ) : (
              <button
                onClick={handleRegenerateBracket}
                disabled={loading}
                className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                <RefreshCw size={18} />
                {loading ? 'Regenerating...' : 'Regenerate Bracket'}
              </button>
            )}

            {!bracketGenerated && !registrationLocked && participantCount >= 2 && (
              <p className="text-xs text-blue-400 mt-2 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>Generating the bracket will automatically lock registration and activate the tournament</span>
              </p>
            )}
          </div>

          {bracketGenerated && sortedRounds.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Trophy size={18} />
                Match Management
              </h3>
              {sortedRounds.map(round => (
                <RoundSection
                  key={round}
                  roundNumber={round}
                  totalRounds={totalRounds}
                  matches={roundGroups[round]}
                  onForfeit={handleForfeitRequest}
                  onReset={handleResetRequest}
                  loading={loading}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-2">{confirmModal.title}</h3>
            <p className="text-gray-300 text-sm mb-6">{confirmModal.message}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium ${confirmModal.confirmClass}`}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminBracketManager;
